import { useEffect, useRef, useState } from 'react'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import {
  addToaster,
  API_END_POINT,
  axiosInstance,
  createSubResource,
  type DataTypeStore,
  getCategoryDropdownData,
  getGenericEntityListingAPI,
  getLayoutByEntityNameAndTypeApi,
  hideLoader,
  LayoutColumnType,
  LayoutResponseType,
  showLoader,
} from '@app-store'
import {
  Checkbox,
  ControlledDropDown,
  CustomButton,
  FormInput,
  PhoneInput,
  DropdownType,
  ViewComponent,
} from '@ui-kit'
import moment from 'moment'
import { v4 as uuid } from 'uuid'
import {
  ENTITY_NAME,
  LAYOUT_TYPE_NAMESPACE,
  MODE_CONSTANTS,
  NAMESPACES,
  RECORD_STATE,
  VERBAL_CONSENT_CATEGORY_KEYS,
  componentNames,
} from '../../utils/constants'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import {
  calculateExpirationDate,
  isCalculableDurationKind,
  normalizeDurationType,
  normalizeDurationValue,
  normalizeStartDate,
  parseDurationKind,
} from '../../form-behavior/calculateExpirationDate.utils'
import MultiUploadWrapper from '../MultiUploadWrapper'
import LookupRender from '../LookupRender'
import { ElementType, LayoutDataObject } from '../../types/layout.types'
import { getEntityDataForUpload } from '../../apis/fetchPresignedUrl.api'
import { editEntityData, getEntityData } from '../../apis/entityData.api'
import { STRINGS, VERBAL_CONSENT_STRINGS } from '../../utils/strings'
import { getElementFromAddedFields } from '../../utils/form.utils'
import { generateLabelFromSelectedFields } from '../../utils/listing.utils'
import {
  CaptureVerbalConsentFormValues,
  CaptureVerbalConsentTaskProps,
  ScriptTemplateDocument,
  VerbalConsentConfigRow,
} from '../../types/verbalConsent.types'

const toCategoryOptions = (
  items?: { id: number; value: string; label?: string | null }[]
): DropdownType<string>[] =>
  (items ?? []).map((item) => ({
    label: item.label || item.value,
    value: item.value,
  }))

const mapProductConsentToConfigRow = (
  data: FieldValues,
  fallbackId: number
): VerbalConsentConfigRow => {
  const templateId = data.script_template_id ?? data.script_template?.id ?? null
  return {
    id: data.id != null ? Number(data.id) : fallbackId,
    name: data.name ?? '',
    script_version: data.script_version ?? null,
    script_template_id: templateId != null ? Number(templateId) : null,
    script_required: data.script_required ?? null,
    duration_type: data.duration_type ?? null,
    duration_value: data.duration_value ?? null,
    product: data.product ?? null,
  }
}

const CONSENT_DECISION_NO = 'No'
const CONSENT_DECISION_YES = 'Yes'

const SCRIPT_TEMPLATE_ELEMENT = {
  id: 0,
  elementId: 'script_template',
  keyName: 'script_template',
  elementName: componentNames.FILE_UPLOAD,
  isDisabled: true,
  meta: {
    label: VERBAL_CONSENT_STRINGS.SCRIPT_TEMPLATE,
    required: false,
    canView: true,
    canEdit: false,
    disabled: true,
    description: null,
    max_value: '5120',
  },
} as ElementType

const EMPTY_FORM_VALUES: CaptureVerbalConsentFormValues = {
  record_id: '',
  consent_type: '',
  script_read: '',
  consent_decision: '',
  decision_date_time: '',
  script_template: '',
  script_version: '',
  start_date: '',
  expiration_date: '',
  consenter_type: '',
  patient_relationship: '',
  consenting_provider: '',
  consenter_first_name: '',
  consenter_last_name: '',
  consenter_phone: '',
  information_sharing_first_name: '',
  information_sharing_last_name: '',
  notes: '',
  revoked: false,
}

const getNestedValue = (field: unknown): string => {
  if (field == null) return ''
  if (typeof field === 'string' || typeof field === 'number') {
    return String(field)
  }
  if (typeof field === 'object' && 'value' in field) {
    const value = (field as { value?: string | number | null }).value
    return value == null ? '' : String(value)
  }
  return ''
}

const getNestedId = (field: unknown): string => {
  if (field == null || typeof field !== 'object' || !('id' in field)) return ''
  const id = (field as { id?: string | number | null }).id
  return id == null ? '' : String(id)
}

const getRelatedName = (field: unknown, fallback?: string): string => {
  if (field && typeof field === 'object') {
    const related = field as { name?: string | null; label?: string | null }
    if (related.name) return String(related.name)
    if (related.label) return String(related.label)
  }
  return fallback ?? ''
}

const toDateInputValue = (value?: string | null, withTime = false) => {
  if (!value) return ''
  const parsed = moment(value)
  if (!parsed.isValid()) return ''
  return parsed.format(withTime ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD')
}

const CaptureVerbalConsentTask: React.FC<CaptureVerbalConsentTaskProps> = ({
  task,
  caseRecordId,
  isDisabled,
  updateTaskResource,
}) => {
  const dispatch = useAppDispatch()
  const formId = `capture-verbal-consent-form-${task?.id}`
  const providerLookupFormId = `capture-verbal-consent-lookup-${task?.id}`
  const [savedConsentId, setSavedConsentId] = useState(
    task?.resource_id ? String(task.resource_id) : ''
  )
  const [isViewMode, setIsViewMode] = useState(!!task?.resource_id)
  const [consentTypeOptions, setConsentTypeOptions] = useState<
    DropdownType<string>[]
  >([])
  const [savedConsentTypeOption, setSavedConsentTypeOption] =
    useState<DropdownType<string> | null>(null)
  const [verbalConsentRows, setVerbalConsentRows] = useState<
    VerbalConsentConfigRow[]
  >([])
  const [scriptTemplateDocument, setScriptTemplateDocument] =
    useState<ScriptTemplateDocument | null>(null)
  const hydratedFormRef = useRef<CaptureVerbalConsentFormValues | null>(null)
  const hydratedScriptDocRef = useRef<ScriptTemplateDocument | null>(null)
  const hasFetchedSavedConsentRef = useRef(false)
  const inactiveConfigFetchedRef = useRef<string | null>(null)
  const [hasFetchedVerbalConsent, setHasFetchedVerbalConsent] = useState(false)
  const isViewModeRef = useRef(isViewMode)
  isViewModeRef.current = isViewMode

  const methods = useForm<CaptureVerbalConsentFormValues>({
    defaultValues: EMPTY_FORM_VALUES,
  })

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    clearErrors,
    formState: { errors },
  } = methods

  const [patientData, setPatientData] = useState<{
    id: number
    stateId: number
  } | null>(null)

  const [layoutData, setLayoutData] = useState<{
    layout?: LayoutDataObject
    entityId?: number
    layoutId?: string
    providerElement?: ElementType | null
  } | null>()

  const interactionRecordId = useAppSelector(
    (state) => state.awsCall.awsConnectId
  )

  const programId = useAppSelector(
    (state) => state.workflowData?.caseData?.programs?.id
  )
  const programName = useAppSelector(
    (state) => state.workflowData?.caseData?.programs?.operational_program_name
  )
  const productId = useAppSelector(
    (state) => state.workflowData?.caseData?.products?.id
  )
  const attestationId = useAppSelector(
    (state) =>
      state.categoryDropdown.categoryMap[
        VERBAL_CONSENT_CATEGORY_KEYS.ATTESTATION_METHOD
      ]?.find((item) => item.value === 'Verbal')?.id
  )
  const categoryMap = useAppSelector(
    (state) => state.categoryDropdown.categoryMap
  )
  const scriptReadOptions = toCategoryOptions(
    categoryMap[VERBAL_CONSENT_CATEGORY_KEYS.SCRIPT_READ]
  )
  const consentDecisionOptions = toCategoryOptions(
    categoryMap[VERBAL_CONSENT_CATEGORY_KEYS.YES_NO]
  )
  const consenterTypeOptions = toCategoryOptions(
    categoryMap[VERBAL_CONSENT_CATEGORY_KEYS.CONSENTER_TYPE]
  )
  const patientRelationshipOptions = toCategoryOptions(
    categoryMap[VERBAL_CONSENT_CATEGORY_KEYS.RELATIONSHIP_WITH_PATIENT]
  )

  const scriptRead = watch('script_read')
  const consentDecision = watch('consent_decision')
  const consenterType = watch('consenter_type')
  const consentType = watch('consent_type')
  const startDate = watch('start_date')

  const hideScriptFields =
    scriptRead === VERBAL_CONSENT_STRINGS.SCRIPT_READ_NO_SCRIPT
  const shouldPopulateScriptFields =
    scriptRead === VERBAL_CONSENT_STRINGS.SCRIPT_READ_YES ||
    scriptRead === VERBAL_CONSENT_STRINGS.SCRIPT_READ_NO
  const isConsentDeclined = consentDecision === CONSENT_DECISION_NO
  const isConsentAccepted = consentDecision === CONSENT_DECISION_YES
  const isConsenterOther =
    consenterType === VERBAL_CONSENT_STRINGS.CONSENTER_TYPE_OTHER
  const showAsView = isViewMode || !!isDisabled
  const selectedConsentConfig = verbalConsentRows.find(
    (row) => String(row.id) === consentType
  )
  const durationKind = parseDurationKind(selectedConsentConfig?.duration_type)
  const hasStartDate = !!normalizeStartDate(startDate)
  const isExpirationManual = durationKind === 'MANUAL'
  const isExpirationDisabled =
    isConsentDeclined || !hasStartDate || !isExpirationManual

  const consentTypeDisplayOptions =
    consentTypeOptions.length > 0
      ? consentTypeOptions.some(
          (option) => option.value === savedConsentTypeOption?.value
        )
        ? consentTypeOptions
        : savedConsentTypeOption
        ? [savedConsentTypeOption, ...consentTypeOptions]
        : consentTypeOptions
      : savedConsentTypeOption
      ? [savedConsentTypeOption]
      : []

  const getOptionLabel = (
    options: DropdownType<string>[],
    value: string | undefined
  ) => options.find((option) => option.value === value)?.label ?? value

  const formatViewDate = (value: string | undefined, withTime = false) => {
    if (!value) return ''
    const parsed = moment(value)
    if (!parsed.isValid()) return value
    return parsed.format(withTime ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY')
  }

  const toViewValue = (value?: string | null) => {
    if (value == null) return '--'
    const trimmed = String(value).trim()
    return trimmed === '' ? '--' : trimmed
  }

  const findCategoryId = (categoryKey: string, value: string) =>
    categoryMap?.[categoryKey]?.find((item) => item.value === value)?.id

  const applyScriptDefaults = async () => {
    if (!shouldPopulateScriptFields || !consentType) {
      setScriptTemplateDocument(null)
      setValue('script_template', '', { shouldValidate: true })
      setValue('script_version', '', { shouldValidate: true })
      return
    }
    const selected = verbalConsentRows.find(
      (row) => String(row.id) === consentType
    )
    setValue('script_version', selected?.script_version ?? '', {
      shouldValidate: true,
    })
    const templateId = selected?.script_template_id
    if (templateId == null) {
      setScriptTemplateDocument(null)
      setValue('script_template', '', { shouldValidate: true })
      return
    }
    setValue('script_template', String(templateId), { shouldValidate: true })
    try {
      const { data } = await getEntityDataForUpload(
        STRINGS.MULTI_UPLOAD_FIELDS_AE_PC_REQUIRED,
        templateId
      )
      setScriptTemplateDocument(data ?? null)
    } catch (error) {
      console.error(error)
      setScriptTemplateDocument(null)
    }
  }

  const mapSavedConsentToForm = (
    record: FieldValues
  ): CaptureVerbalConsentFormValues => {
    const provider = record[VERBAL_CONSENT_STRINGS.PROVIDER_KEY_NAME]
    const providerId = getNestedId(provider)
    const providerRecord =
      providerId && provider && typeof provider === 'object'
        ? ({
            ...(provider as DataTypeStore),
            id: Number(providerId),
          } as DataTypeStore)
        : null
    const providerLabel =
      layoutData?.providerElement && providerRecord
        ? generateLabelFromSelectedFields(
            layoutData.providerElement.meta?.selected_fields ?? [],
            providerRecord,
            layoutData.providerElement.meta?.relationship?.target_entity_id ??
              ''
          )
        : ''

    return {
      ...EMPTY_FORM_VALUES,
      record_id: record.record_id ?? '',
      consent_type: getNestedId(record.consent_type),
      script_read: getNestedValue(record.script_read),
      consent_decision: getNestedValue(record.consent_decision),
      decision_date_time: toDateInputValue(record.decision_date_time, true),
      script_template: getNestedId(record.script_template),
      script_version: record.script_version ?? '',
      start_date: toDateInputValue(record.start_date),
      expiration_date: toDateInputValue(record.expiration_date),
      consenter_type: getNestedValue(record.consenter_type),
      patient_relationship: getNestedValue(
        record.relationship_to_patient ?? record.patient_relationship
      ),
      consenting_provider: providerRecord ?? '',
      lookupLabels: {
        [VERBAL_CONSENT_STRINGS.PROVIDER_KEY_NAME]: providerLabel,
        consent_type: getRelatedName(
          record.consent_type,
          record.disabled_consent_type_name
        ),
      },
      consenter_first_name: record.consenter_first_name ?? '',
      consenter_last_name: record.consenter_last_name ?? '',
      consenter_phone: record.consenter_phone ?? '',
      information_sharing_first_name:
        record.information_sharing_first_name ?? '',
      information_sharing_last_name: record.information_sharing_last_name ?? '',
      notes: record.notes ?? '',
      revoked: !!record.revoked,
    }
  }

  const applySavedScriptTemplate = async (scriptTemplate: unknown) => {
    if (!scriptTemplate) {
      setScriptTemplateDocument(null)
      hydratedScriptDocRef.current = null
      return
    }
    if (
      typeof scriptTemplate === 'object' &&
      scriptTemplate &&
      'attachment' in scriptTemplate
    ) {
      const document = scriptTemplate as ScriptTemplateDocument
      setScriptTemplateDocument(document)
      hydratedScriptDocRef.current = document
      return
    }
    const templateId = getNestedId(scriptTemplate)
    if (!templateId) {
      setScriptTemplateDocument(null)
      hydratedScriptDocRef.current = null
      return
    }
    try {
      const { data } = await getEntityDataForUpload(
        STRINGS.MULTI_UPLOAD_FIELDS_AE_PC_REQUIRED,
        Number(templateId)
      )
      setScriptTemplateDocument(data ?? null)
      hydratedScriptDocRef.current = data ?? null
    } catch (error) {
      console.error(error)
      setScriptTemplateDocument(null)
      hydratedScriptDocRef.current = null
    }
  }

  const fetchSavedConsent = async () => {
    if (
      !savedConsentId ||
      !layoutData?.entityId ||
      hasFetchedSavedConsentRef.current
    ) {
      return
    }
    hasFetchedSavedConsentRef.current = true
    try {
      dispatch(showLoader())
      const response = await getEntityData(
        ENTITY_NAME.PATIENT_CONSENTS,
        Number(layoutData.entityId),
        Number(layoutData.layoutId || 0),
        savedConsentId
      )
      const record = response?.data
      if (!record) return
      const formValues = mapSavedConsentToForm(record)
      const consentTypeId = formValues.consent_type
      const consentTypeName = getRelatedName(
        record.consent_type,
        record.disabled_consent_type_name
      )
      if (consentTypeId && consentTypeName) {
        setSavedConsentTypeOption({
          value: consentTypeId,
          label: consentTypeName,
        })
      }
      hydratedFormRef.current = formValues
      reset(formValues)
      await applySavedScriptTemplate(record.script_template)
      setIsViewMode(true)
    } catch (error) {
      console.error(error)
      hasFetchedSavedConsentRef.current = false
    } finally {
      dispatch(hideLoader())
    }
  }

  const fetchLayoutData = async () => {
    try {
      const layoutResponse = await getLayoutByEntityNameAndTypeApi({
        entityName: ENTITY_NAME.PATIENT_CONSENTS,
        type: LAYOUT_TYPE_NAMESPACE.VIEW_EDIT_TYPE,
        isDefault: true,
      })
      const layout = layoutResponse?.data?.[0]
      const entityId = layout?.entity_id?.toString() ?? ''
      const layoutId = layout?.id?.toString() ?? ''

      let providerElement = null
      layout?.layout_data?.pages[0]?.rows[0]?.columns?.forEach(
        (column: LayoutColumnType) => {
          const el = column.elements.find(
            (element) =>
              element.keyName === VERBAL_CONSENT_STRINGS.PROVIDER_KEY_NAME
          )
          if (el) {
            providerElement = el
          }
        }
      )
      if (providerElement) {
        providerElement = getElementFromAddedFields(
          providerElement,
          entityId,
          layout?.layout_data?.added_fields
        )
      }
      setLayoutData({ layout, entityId, layoutId, providerElement })
    } catch (error) {
      console.error(error)
    }
  }

  const fetchPatientData = async () => {
    try {
      const response = await getGenericEntityListingAPI(
        {
          entity_name: ENTITY_NAME.CASE_LOWER,
          select: ['patient'],
          related_entities: {
            [ENTITY_NAME.PATIENT]: {
              select: ['c_patient_addresses_c_patients'],
              related_entities: {
                ['c_patient_addresses_c_patients']: {
                  select: ['state', 'is_preferred'],
                },
              },
            },
          },
        },
        caseRecordId ?? ''
      )
      setPatientData({
        id: response?.patient?.id,
        stateId:
          response?.patient?.c_patient_addresses_c_patients?.[0]?.state?.id,
      })
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    fetchLayoutData()
    fetchPatientData()
    Object.values(VERBAL_CONSENT_CATEGORY_KEYS).forEach((categoryKey) => {
      dispatch(getCategoryDropdownData(categoryKey))
    })
  }, [])

  useEffect(() => {
    fetchSavedConsent()
  }, [savedConsentId, layoutData?.entityId, layoutData?.layoutId])

  const fetchConsentData = async () => {
    if (!programId || !patientData?.id || !productId) return
    try {
      const response = await axiosInstance.get(
        `${API_END_POINT.CORE}consents/verbal`,
        {
          params: {
            programId,
            patientId: patientData?.id,
            productId,
          },
        }
      )

      const rows = (
        Array.isArray(response?.data) ? response.data : []
      ) as VerbalConsentConfigRow[]
      setVerbalConsentRows(rows)
      setConsentTypeOptions(
        rows
          .filter((item) => item?.id != null && item?.name)
          .map((item) => ({
            label: item.name,
            value: String(item.id),
          }))
      )
    } catch (error) {
      console.error(error)
      setVerbalConsentRows([])
      setConsentTypeOptions([])
    } finally {
      setHasFetchedVerbalConsent(true)
    }
  }

  const fetchInactiveConsentConfig = async (consentTypeId: string) => {
    if (!consentTypeId || inactiveConfigFetchedRef.current === consentTypeId) {
      return
    }
    inactiveConfigFetchedRef.current = consentTypeId
    try {
      const response = await axiosInstance.post(
        `${API_END_POINT.ENTITY_MANAGER_V3}entity-data/get/${ENTITY_NAME.PRODUCT_CONSENTS}/${consentTypeId}`,
        {
          select: [
            'duration_type',
            'duration_value',
            'name',
            'product',
            'script_version',
            'script_template_id',
            'script_required',
          ],
          entity_name: ENTITY_NAME.PRODUCT_CONSENTS,
        }
      )
      const data = response?.data
      if (!data) return

      const configRow = mapProductConsentToConfigRow(
        data,
        Number(consentTypeId)
      )
      setVerbalConsentRows((prev) => {
        if (prev.some((row) => String(row.id) === String(configRow.id))) {
          return prev
        }
        return [...prev, configRow]
      })
      if (configRow.name) {
        setConsentTypeOptions((prev) => {
          if (prev.some((option) => option.value === String(configRow.id))) {
            return prev
          }
          return [
            ...prev,
            { label: configRow.name, value: String(configRow.id) },
          ]
        })
        setSavedConsentTypeOption((prev) =>
          prev?.value === String(configRow.id)
            ? { value: String(configRow.id), label: configRow.name }
            : prev
        )
      }
    } catch (error) {
      console.error(error)
      inactiveConfigFetchedRef.current = null
    }
  }

  useEffect(() => {
    setHasFetchedVerbalConsent(false)
    inactiveConfigFetchedRef.current = null
    fetchConsentData()
  }, [programId, patientData?.id, productId])

  useEffect(() => {
    if (!savedConsentId || !hasFetchedVerbalConsent) return
    const savedTypeId =
      savedConsentTypeOption?.value || (consentType ? String(consentType) : '')
    if (!savedTypeId) return
    const existsInVerbal = verbalConsentRows.some(
      (row) => String(row.id) === savedTypeId
    )
    if (existsInVerbal) return
    fetchInactiveConsentConfig(savedTypeId)
  }, [
    savedConsentId,
    savedConsentTypeOption?.value,
    consentType,
    verbalConsentRows,
    hasFetchedVerbalConsent,
  ])

  useEffect(() => {
    if (savedConsentId) return
    if (interactionRecordId) {
      setValue('record_id', interactionRecordId)
    }
  }, [interactionRecordId, savedConsentId, setValue])

  useEffect(() => {
    if (isViewModeRef.current) return
    if (consentDecision) {
      setValue('decision_date_time', moment().format('YYYY-MM-DDTHH:mm'))
    }
  }, [consentDecision, setValue])

  useEffect(() => {
    if (isViewModeRef.current) return
    applyScriptDefaults()
  }, [scriptRead, consentType, verbalConsentRows])

  useEffect(() => {
    if (isViewModeRef.current) return
    if (isConsentDeclined) {
      setValue('start_date', '', { shouldValidate: false })
      setValue('expiration_date', '', { shouldValidate: false })
      clearErrors([
        'start_date',
        'expiration_date',
        'consenter_type',
        'patient_relationship',
        'consenter_first_name',
        'consenter_last_name',
      ])
      return
    }
    if (durationKind === 'INDEFINITE') {
      setValue('expiration_date', '')
      return
    }
    const normalizedStart = normalizeStartDate(startDate)
    if (durationKind === 'MANUAL') {
      if (!normalizedStart) {
        setValue('expiration_date', '')
        return
      }
      const currentExpiration = normalizeStartDate(getValues('expiration_date'))
      if (currentExpiration && currentExpiration < normalizedStart) {
        setValue('expiration_date', '')
      }
      return
    }
    if (!isCalculableDurationKind(durationKind)) {
      return
    }
    const durationType = normalizeDurationType(durationKind)
    const durationValue = normalizeDurationValue(
      selectedConsentConfig?.duration_value
    )
    if (!durationType || durationValue == null || !normalizedStart) {
      setValue('expiration_date', '')
      return
    }
    try {
      setValue(
        'expiration_date',
        calculateExpirationDate(durationType, durationValue, normalizedStart),
        { shouldValidate: true }
      )
    } catch (error) {
      console.error(error)
      setValue('expiration_date', '')
    }
  }, [
    durationKind,
    getValues,
    isConsentDeclined,
    selectedConsentConfig?.duration_value,
    setValue,
    startDate,
    clearErrors,
  ])

  const buildConsentPayload = (data: FieldValues) => {
    const scriptReadId = findCategoryId(
      VERBAL_CONSENT_CATEGORY_KEYS.SCRIPT_READ,
      data.script_read
    )
    const consentDecisionId = findCategoryId(
      VERBAL_CONSENT_CATEGORY_KEYS.YES_NO,
      data.consent_decision
    )
    const consenterTypeId = findCategoryId(
      VERBAL_CONSENT_CATEGORY_KEYS.CONSENTER_TYPE,
      data.consenter_type
    )
    const relationshipId = findCategoryId(
      VERBAL_CONSENT_CATEGORY_KEYS.RELATIONSHIP_WITH_PATIENT,
      data.patient_relationship
    )

    const isDeclined = data.consent_decision === CONSENT_DECISION_NO

    const payload: FieldValues = {
      record_id: data.record_id || '',
      start_date: isDeclined ? null : data.start_date || null,
      expiration_date: isDeclined ? null : data.expiration_date || null,
      decision_date_time: data.decision_date_time
        ? moment(data.decision_date_time).toISOString()
        : undefined,
      ...(scriptTemplateDocument?.id
        ? {
            script_template: {
              id: scriptTemplateDocument.id,
              access_type: scriptTemplateDocument.access_type ?? 'PUBLIC',
              record_status: scriptTemplateDocument.record_status ?? 'ACTIVE',
              attachment: (scriptTemplateDocument.attachment ?? []).map(
                (file) => ({
                  name: file.name,
                  path: file.path,
                  size: String(file.size ?? ''),
                  type: file.type ?? 'd_file_upload',
                  alias: file.alias,
                  _state: RECORD_STATE.CHANGED,
                  reason: file.reason ?? null,
                  status: file.status,
                  tagging: file.tagging ?? null,
                  field_id: file.field_id,
                  pdf_type: null,
                  entity_id: file.entity_id,
                  extension: file.extension ?? null,
                  created_at: file.created_at ?? null,
                  created_by: file.created_by ?? null,
                  access_type: file.access_type,
                  modified_at: file.modified_at ?? null,
                  modified_by: file.modified_by ?? null,
                  content_type: file.content_type,
                })
              ),
            },
          }
        : {}),
      script_version: data.script_version || '',
      consenter_first_name: data.consenter_first_name || '',
      consenter_last_name: data.consenter_last_name || '',
      consenter_phone: data.consenter_phone || '',
      information_sharing_first_name: data.information_sharing_first_name || '',
      information_sharing_last_name: data.information_sharing_last_name || '',
      notes: data.notes || '',
      revoked: !!data.revoked,
      ...(programId ? { programs: { id: Number(programId) } } : {}),
      ...(selectedConsentConfig?.product?.id
        ? { product: { id: Number(selectedConsentConfig.product.id) } }
        : {}),
      ...(patientData?.id ? { patient: { id: Number(patientData?.id) } } : {}),
      ...(caseRecordId ? { case: { id: Number(caseRecordId) } } : {}),
      ...(data.consent_type
        ? { consent_type: { id: Number(data.consent_type) } }
        : {}),
      ...(scriptReadId ? { script_read: { id: scriptReadId } } : {}),
      ...(consentDecisionId
        ? { consent_decision: { id: consentDecisionId } }
        : {}),
      ...(consenterTypeId ? { consenter_type: { id: consenterTypeId } } : {}),
      ...(relationshipId
        ? { relationship_to_patient: { id: relationshipId } }
        : {}),
      ...(attestationId
        ? { consent_attestation_method: { id: attestationId } }
        : {}),
      ...(data[VERBAL_CONSENT_STRINGS.PROVIDER_KEY_NAME]?.id
        ? {
            [VERBAL_CONSENT_STRINGS.PROVIDER_KEY_NAME]: {
              id: Number(data[VERBAL_CONSENT_STRINGS.PROVIDER_KEY_NAME].id),
            },
          }
        : {}),
      ...(patientData?.stateId
        ? { c_states: { id: Number(patientData.stateId) } }
        : {}),
      ...(programName
        ? { disabled_programs_operational_program_name: programName }
        : {}),
      ...(selectedConsentConfig?.name
        ? { disabled_consent_type_name: selectedConsentConfig.name }
        : {}),
    }

    return payload
  }

  const onSubmit = async (data: FieldValues) => {
    try {
      dispatch(showLoader())

      const parentCaseId = caseRecordId || task?.case_id
      if (!parentCaseId) {
        dispatch(
          addToaster({
            id: uuid(),
            type: 'error',
            message: NAMESPACES.SOMETHING_WENT_WRONG,
          })
        )
        return
      }

      const payload = buildConsentPayload(data)
      let resourceId = savedConsentId

      if (resourceId) {
        await editEntityData(
          ENTITY_NAME.PATIENT_CONSENTS,
          Number(layoutData?.entityId ?? 0),
          Number(layoutData?.layoutId || 0),
          { ...payload, id: resourceId },
          resourceId
        )
      } else {
        const response = await createSubResource(
          ENTITY_NAME.PATIENT_CONSENTS,
          String(layoutData?.entityId ?? ''),
          '',
          String(parentCaseId),
          '',
          payload
        )
        resourceId = response?.data?.id ? String(response.data.id) : ''
        if (resourceId) {
          await updateTaskResource?.(task, {
            resource_id: resourceId,
          })
          setSavedConsentId(resourceId)
          hasFetchedSavedConsentRef.current = true
        }
      }

      dispatch(
        addToaster({
          id: uuid(),
          type: 'success',
          message:
            data.consent_decision === CONSENT_DECISION_YES
              ? VERBAL_CONSENT_STRINGS.TOAST_YES
              : VERBAL_CONSENT_STRINGS.TOAST_NO,
        })
      )

      const savedFormValues = {
        ...EMPTY_FORM_VALUES,
        ...data,
        lookupLabels: data.lookupLabels ?? getValues('lookupLabels'),
      } as CaptureVerbalConsentFormValues
      hydratedFormRef.current = savedFormValues
      hydratedScriptDocRef.current = scriptTemplateDocument
      setIsViewMode(true)
    } catch (error) {
      console.error(error)
      dispatch(
        addToaster({
          id: uuid(),
          type: 'error',
          message: NAMESPACES.SOMETHING_WENT_WRONG,
        })
      )
    } finally {
      dispatch(hideLoader())
    }
  }

  const handleCancel = () => {
    if (savedConsentId) {
      if (hydratedFormRef.current) {
        reset(hydratedFormRef.current)
        setScriptTemplateDocument(hydratedScriptDocRef.current)
      }
      setIsViewMode(true)
      return
    }
    reset({
      ...EMPTY_FORM_VALUES,
      record_id: interactionRecordId ?? '',
    })
    setScriptTemplateDocument(null)
  }

  const handleEditClick = (e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    setIsViewMode(false)
  }

  return (
    <div className="p-4">
      <div id={providerLookupFormId} />
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(onSubmit)} id={formId}>
          <div className="flex justify-end gap-2 pb-4">
            {!isDisabled && (
              <>
                {!isViewMode && (
                  <CustomButton
                    type="button"
                    onClick={handleCancel}
                    dataQaId="verbal-consent-cancel"
                  >
                    {NAMESPACES.CANCEL_BUTTON}
                  </CustomButton>
                )}
                <CustomButton
                  type={isViewMode ? 'button' : 'submit'}
                  form={isViewMode ? undefined : formId}
                  onClick={isViewMode ? handleEditClick : undefined}
                  isFilled
                  dataQaId={
                    isViewMode
                      ? 'verbal-consent-edit'
                      : savedConsentId
                      ? 'verbal-consent-update'
                      : 'verbal-consent-add'
                  }
                >
                  {isViewMode
                    ? NAMESPACES.EDIT
                    : savedConsentId
                    ? NAMESPACES.UPDATE_BUTTON
                    : NAMESPACES.ADD}
                </CustomButton>
              </>
            )}
          </div>

          <div className="grid grid-cols-4 gap-4">
            {showAsView ? (
              <ViewComponent
                label={VERBAL_CONSENT_STRINGS.RECORD_ID}
                data={toViewValue(watch('record_id'))}
              />
            ) : (
              <FormInput
                register={register}
                name="record_id"
                label={VERBAL_CONSENT_STRINGS.RECORD_ID}
                disabled={!!interactionRecordId}
              />
            )}

            {showAsView ? (
              <ViewComponent
                label={VERBAL_CONSENT_STRINGS.CONSENT_TYPE}
                data={toViewValue(
                  consentTypeDisplayOptions.find(
                    (option) => option.value === consentType
                  )?.label || watch('lookupLabels')?.consent_type
                )}
              />
            ) : (
              <ControlledDropDown
                control={control}
                name="consent_type"
                label={VERBAL_CONSENT_STRINGS.CONSENT_TYPE}
                options={consentTypeDisplayOptions}
                required
                errors={errors}
                onChange={(e: DropdownType<string>) => {
                  const value = e?.value ?? ''
                  setValue('consent_type', value, {
                    shouldValidate: true,
                  })
                }}
              />
            )}

            {showAsView ? (
              <ViewComponent
                label={VERBAL_CONSENT_STRINGS.SCRIPT_READ}
                data={toViewValue(
                  getOptionLabel(scriptReadOptions, scriptRead)
                )}
              />
            ) : (
              <ControlledDropDown
                control={control}
                name="script_read"
                label={VERBAL_CONSENT_STRINGS.SCRIPT_READ}
                options={scriptReadOptions}
                required
                errors={errors}
                onChange={(e: DropdownType<string>) => {
                  setValue('script_read', e?.value ?? '', {
                    shouldValidate: true,
                  })
                }}
              />
            )}

            {showAsView ? (
              <ViewComponent
                label={VERBAL_CONSENT_STRINGS.CONSENT_DECISION}
                data={toViewValue(
                  getOptionLabel(consentDecisionOptions, consentDecision)
                )}
              />
            ) : (
              <ControlledDropDown
                control={control}
                name="consent_decision"
                label={VERBAL_CONSENT_STRINGS.CONSENT_DECISION}
                options={consentDecisionOptions}
                required
                errors={errors}
                onChange={(e: DropdownType<string>) => {
                  setValue('consent_decision', e?.value ?? '', {
                    shouldValidate: true,
                  })
                }}
              />
            )}

            {showAsView ? (
              <ViewComponent
                label={VERBAL_CONSENT_STRINGS.DECISION_DATE_TIME}
                data={toViewValue(
                  formatViewDate(watch('decision_date_time'), true)
                )}
              />
            ) : (
              <FormInput
                register={register}
                name="decision_date_time"
                label={VERBAL_CONSENT_STRINGS.DECISION_DATE_TIME}
                type="datetime-local"
                disabled
              />
            )}

            {!hideScriptFields && (
              <>
                <MultiUploadWrapper
                  key={String(
                    shouldPopulateScriptFields
                      ? selectedConsentConfig?.script_template_id ?? 'empty'
                      : 'empty'
                  )}
                  id={
                    shouldPopulateScriptFields &&
                    selectedConsentConfig?.script_template_id != null
                      ? String(selectedConsentConfig.script_template_id)
                      : String(NAMESPACES.NO_DATA)
                  }
                  element={SCRIPT_TEMPLATE_ELEMENT}
                  register={register}
                  setValue={setValue}
                  errors={errors}
                  mode={MODE_CONSTANTS.VIEW}
                />

                {showAsView ? (
                  <ViewComponent
                    label={VERBAL_CONSENT_STRINGS.SCRIPT_VERSION}
                    data={toViewValue(watch('script_version'))}
                  />
                ) : (
                  <FormInput
                    register={register}
                    name="script_version"
                    label={VERBAL_CONSENT_STRINGS.SCRIPT_VERSION}
                    disabled
                  />
                )}
              </>
            )}

            {showAsView ? (
              <ViewComponent
                label={VERBAL_CONSENT_STRINGS.START_DATE}
                data={toViewValue(formatViewDate(watch('start_date')))}
              />
            ) : (
              <FormInput
                register={register}
                name="start_date"
                label={VERBAL_CONSENT_STRINGS.START_DATE}
                type="date"
                validation={{ required: isConsentAccepted }}
                readOnly={isConsentDeclined}
                className={isConsentDeclined ? 'cursor-not-allowed' : ''}
                errors={errors}
              />
            )}

            {showAsView ? (
              <ViewComponent
                label={VERBAL_CONSENT_STRINGS.EXPIRATION_DATE}
                data={toViewValue(formatViewDate(watch('expiration_date')))}
              />
            ) : (
              <FormInput
                register={register}
                name="expiration_date"
                label={VERBAL_CONSENT_STRINGS.EXPIRATION_DATE}
                type="date"
                min={hasStartDate ? startDate : undefined}
                validation={
                  isConsentAccepted && durationKind !== 'INDEFINITE'
                    ? {
                        required: true,
                        validate: (value: unknown) =>
                          !hasStartDate ||
                          !value ||
                          (typeof value === 'string' && value >= startDate) ||
                          VERBAL_CONSENT_STRINGS.EXPIRATION_BEFORE_START,
                      }
                    : { required: false }
                }
                readOnly={isConsentDeclined}
                disabled={isExpirationDisabled}
                className={isConsentDeclined ? 'cursor-not-allowed' : ''}
                errors={errors}
              />
            )}

            {showAsView ? (
              <ViewComponent
                label={VERBAL_CONSENT_STRINGS.CONSENTER_TYPE}
                data={toViewValue(
                  getOptionLabel(consenterTypeOptions, consenterType)
                )}
              />
            ) : (
              <ControlledDropDown
                control={control}
                name="consenter_type"
                label={VERBAL_CONSENT_STRINGS.CONSENTER_TYPE}
                options={consenterTypeOptions}
                required={isConsentAccepted}
                errors={errors}
                onChange={(e: DropdownType<string>) => {
                  setValue('consenter_type', e?.value ?? '', {
                    shouldValidate: true,
                  })
                }}
              />
            )}

            {showAsView ? (
              <ViewComponent
                label={VERBAL_CONSENT_STRINGS.PATIENT_RELATIONSHIP}
                data={toViewValue(
                  getOptionLabel(
                    patientRelationshipOptions,
                    watch('patient_relationship')
                  )
                )}
              />
            ) : (
              <ControlledDropDown
                control={control}
                name="patient_relationship"
                label={VERBAL_CONSENT_STRINGS.PATIENT_RELATIONSHIP}
                options={patientRelationshipOptions}
                required={isConsenterOther && isConsentAccepted}
                errors={errors}
                onChange={(e: DropdownType<string>) => {
                  setValue('patient_relationship', e?.value ?? '', {
                    shouldValidate: true,
                  })
                }}
              />
            )}

            {showAsView ? (
              <ViewComponent
                label={VERBAL_CONSENT_STRINGS.PROVIDER}
                data={toViewValue(
                  watch('lookupLabels')?.[
                    VERBAL_CONSENT_STRINGS.PROVIDER_KEY_NAME
                  ]
                )}
              />
            ) : (
              layoutData?.providerElement && (
                <LookupRender
                  element={layoutData.providerElement}
                  errors={errors}
                  control={control}
                  setValue={setValue}
                  getValues={getValues}
                  fullLayout={
                    layoutData.layout as unknown as LayoutResponseType
                  }
                  formUniqueId={providerLookupFormId}
                  mode={
                    savedConsentId ? MODE_CONSTANTS.EDIT : MODE_CONSTANTS.ADD
                  }
                  dataQaId={VERBAL_CONSENT_STRINGS.PROVIDER_KEY_NAME}
                  disabled={false}
                  required={false}
                />
              )
            )}

            {showAsView ? (
              <ViewComponent
                label={VERBAL_CONSENT_STRINGS.CONSENTER_FIRST_NAME}
                data={toViewValue(watch('consenter_first_name'))}
              />
            ) : (
              <FormInput
                register={register}
                name="consenter_first_name"
                label={VERBAL_CONSENT_STRINGS.CONSENTER_FIRST_NAME}
                validation={{
                  required: isConsenterOther && isConsentAccepted,
                }}
                errors={errors}
              />
            )}

            {showAsView ? (
              <ViewComponent
                label={VERBAL_CONSENT_STRINGS.CONSENTER_LAST_NAME}
                data={toViewValue(watch('consenter_last_name'))}
              />
            ) : (
              <FormInput
                register={register}
                name="consenter_last_name"
                label={VERBAL_CONSENT_STRINGS.CONSENTER_LAST_NAME}
                validation={{
                  required: isConsenterOther && isConsentAccepted,
                }}
                errors={errors}
              />
            )}

            {showAsView ? (
              <ViewComponent
                label={VERBAL_CONSENT_STRINGS.CONSENTER_PHONE}
                data={toViewValue(watch('consenter_phone'))}
                isPhone
              />
            ) : (
              <PhoneInput
                register={register}
                name="consenter_phone"
                label={VERBAL_CONSENT_STRINGS.CONSENTER_PHONE}
                errors={errors}
                setValue={setValue}
                control={control}
              />
            )}

            {showAsView ? (
              <ViewComponent
                label={VERBAL_CONSENT_STRINGS.INFO_SHARING_FIRST_NAME}
                data={toViewValue(watch('information_sharing_first_name'))}
              />
            ) : (
              <FormInput
                register={register}
                name="information_sharing_first_name"
                label={VERBAL_CONSENT_STRINGS.INFO_SHARING_FIRST_NAME}
              />
            )}

            {showAsView ? (
              <ViewComponent
                label={VERBAL_CONSENT_STRINGS.INFO_SHARING_LAST_NAME}
                data={toViewValue(watch('information_sharing_last_name'))}
              />
            ) : (
              <FormInput
                register={register}
                name="information_sharing_last_name"
                label={VERBAL_CONSENT_STRINGS.INFO_SHARING_LAST_NAME}
              />
            )}

            {showAsView ? (
              <ViewComponent
                label={VERBAL_CONSENT_STRINGS.NOTES}
                data={toViewValue(watch('notes'))}
              />
            ) : (
              <FormInput
                register={register}
                name="notes"
                label={VERBAL_CONSENT_STRINGS.NOTES}
              />
            )}

            {showAsView ? (
              <ViewComponent
                label={VERBAL_CONSENT_STRINGS.REVOKED}
                data={watch('revoked') ? NAMESPACES.YES : NAMESPACES.NO}
              />
            ) : (
              <div className="flex items-end pb-2">
                <Checkbox
                  register={register}
                  name="revoked"
                  label={VERBAL_CONSENT_STRINGS.REVOKED}
                />
              </div>
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  )
}

export default CaptureVerbalConsentTask
